import React from 'react';
import { useTranslation } from 'react-i18next';

import CustomTimelineChart from '../../../../../../shared-components/custom-timeline-chart/CustomTimelineChart';

interface StatusTimelineItem {
  _id?: string;
  chargingPointSn?: string;
  connector?: number | string;
  status: string;
  startsAt: string;
  endsAt: string | null;
  startsAtAdjusted?: string;
  endsAtAdjusted?: string | null;
}

interface ChargingPointConnectorStatusProperties {
  _id: number;
  connectorStatus: StatusTimelineItem[];
}

interface DeviceStatusProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: string;
  startsAt: string;
  endsAt: string;
  startsAtAdjusted: string;
  endsAtAdjusted: string;
  transactionStartsAtAdjusted?: string;
  transactionEndsAtAdjusted?: string;
}

interface ConsumptionTimelineViewProperties {
  topTimelineData: DeviceStatusProperties[];
  connectorListToRender: ChargingPointConnectorStatusProperties[];
  selectedDateString: string;
  dataLoading: boolean;
  connectorLoading: boolean;
}

/**
 * Consumption Timeline View component rendering consumption chart and connector charts
 * @param {ConsumptionTimelineViewProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered component
 */
function ConsumptionTimelineView(
  properties: ConsumptionTimelineViewProperties,
): React.ReactElement {
  const {
    topTimelineData,
    connectorListToRender,
    selectedDateString,
    dataLoading,
    connectorLoading,
  } = properties;
  const { t } = useTranslation('devices');

  return (
    <>
      <div className="global-timeline-legend-container">
        {/* Device Status Legend */}
        <div className="legend-section">
          <span className="legend-title">{t('Device Status')}</span>
          <div className="legend-row">
            {[
              {
                name: t('powerConsumed'),
                color: 'var(--charging-color)',
              },
              {
                name: t('idle'),
                color: '#B3261E',
              },
            ].map((eachLabel, index) => (
              <div key={index} className="legend-item">
                <div
                  className="legend-color-dot"
                  style={{ backgroundColor: eachLabel.color }}
                />
                <span className="legend-item-name">{eachLabel.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Connections Legend */}
        <div className="legend-section">
          <span className="legend-title">{t('connections', 'Connections')}</span>
          <div className="legend-row">
            {[
              {
                name: t('available'),
                color: 'var(--available-color)',
              },
              {
                name: t('charging'),
                color: 'var(--charging-color)',
              },
              {
                name: t('preparing'),
                color: 'var(--preparing-color)',
              },
              {
                name: t('finishing'),
                color: 'var(--finishing-color)',
              },
              {
                name: t('faulted'),
                color: 'var(--faulted-color)',
              },
              {
                name: t('UnAvailable'),
                color: 'var(--unavailable-color)',
              },
            ].map((eachLabel, index) => (
              <div key={index} className="legend-item">
                <div
                  className="legend-color-dot"
                  style={{ backgroundColor: eachLabel.color }}
                />
                <span className="legend-item-name">{eachLabel.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="device-timeline-view-container">
        <div className="device-timeline-view-content">
          <CustomTimelineChart
            title={t('consumption')}
            timelineData={(Array.isArray(topTimelineData) ? topTimelineData : []).map(
              (eachTimeline) => ({
                start:
                  eachTimeline?.transactionStartsAtAdjusted || eachTimeline?.startsAt,
                end:
                  eachTimeline?.transactionEndsAtAdjusted || eachTimeline?.endsAt,
                status: eachTimeline?.status || (eachTimeline as any)?.state,
              }),
            )}
            timelineLabel={[
              {
                name: t('powerConsumed'),
                color: 'var(--charging-color)',
                key: 'charging',
              },
              {
                name: t('idle'),
                color: '#B3261E',
                key: 'idle',
              },
            ]}
            date={selectedDateString}
            dataLoading={dataLoading}
            showLegend={false}
          />
        </div>
        <div className="device-connector-status-wrap">
          {connectorListToRender?.map((eachConnectorStatus, index) => (
            <div
              className="each-connector-status-container"
              key={eachConnectorStatus?._id ?? index}>
              <CustomTimelineChart
                title={
                  eachConnectorStatus?._id === 0
                    ? t('Charger Status', 'Charger Status')
                    : `${t('connector')} ${eachConnectorStatus?._id ?? index + 1}`
                }
                timelineData={(eachConnectorStatus?.connectorStatus || [])?.map(
                  (eachTimeline) => ({
                    start: eachTimeline?.startsAtAdjusted || eachTimeline?.startsAt,
                    end: eachTimeline?.endsAtAdjusted || eachTimeline?.endsAt,
                    status: eachTimeline?.status,
                  }),
                )}
                timelineLabel={[
                  {
                    name: t('available'),
                    color: 'var(--available-color)',
                    key: 'available',
                  },
                  {
                    name: t('charging'),
                    color: 'var(--charging-color)',
                    key: 'charging',
                  },
                  {
                    name: t('preparing'),
                    color: 'var(--preparing-color)',
                    key: 'preparing',
                  },
                  {
                    name: t('finishing'),
                    color: 'var(--finishing-color)',
                    key: 'finishing',
                  },
                  {
                    name: t('faulted'),
                    color: 'var(--faulted-color)',
                    key: 'faulted',
                  },
                  {
                    name: t('UnAvailable'),
                    color: 'var(--unavailable-color)',
                    key: 'unavailable',
                  },
                ]}
                date={selectedDateString}
                dataLoading={connectorLoading}
                showLegend={false}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default ConsumptionTimelineView;
